import { useEffect, useState } from 'preact/hooks';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { useAuth } from '../../context/AuthContext.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useSignInRedirect } from '../../hooks/useSignInRedirect.js';
import { ApiError, api } from '../../services/api.js';
import { prepareIdentityFile } from '../../utils/imageDownscale.js';
import { DocumentGroupCard } from './identity/DocumentGroupCard.js';
import { DocumentPreviewSheet } from './identity/DocumentPreviewSheet.js';
import {
  IDENTITY_GROUPS,
  type IdentitySide,
  type SideRequirement,
  requirementLabel,
} from './identity/groups.js';
import { type IdentityStatus, statusView } from './identity/status.js';

interface IdentitySummary {
  status: IdentityStatus;
  rejection_reason?: string;
  sides: Record<IdentitySide, boolean>;
  /** 209 (#305): free text, saved independently of any document. */
  student_id?: string;
  /** 209 (#305): required before submission (missing_for_submission), saved independently. */
  driving_licence_expires_at?: string;
  /** 379 (#379): computed server-side (users/identity.ts's own rule) — never re-derived here, so
   *  this screen and the booking refusal cannot disagree about what "expired" means. */
  licence_expired?: boolean;
  /** 181: what the customer still has to provide before they may submit. Empty means they may.
   *  Computed by the platform, never here — the refusal and this screen must not be able to
   *  disagree about what "complete" means (spec FR-005). */
  missing_for_submission?: string[];
  /** 186: how compulsory each side is, so the screen can MARK the must-have documents before
   *  anything is uploaded. `missing_for_submission` cannot answer that — it names what is
   *  outstanding, so it empties as the customer progresses and says nothing per side.
   *  Optional on purpose: an older or cached response marks nothing rather than inventing a rule. */
  side_requirements?: Record<IdentitySide, SideRequirement>;
}

interface UploadResponse {
  side: IdentitySide;
  provided: boolean;
  status: IdentityStatus;
}

interface IdentityPageProps {
  path?: string;
}

/** "NRIC back" — what the preview is titled, built from the same grouping the rows use. */
function sideTitle(side: IdentitySide): string {
  for (const group of IDENTITY_GROUPS) {
    const entry = group.sides.find((s) => s.side === side);
    if (entry) return entry.label ? `${group.label} ${entry.label.toLowerCase()}` : group.label;
  }
  return side;
}

/**
 * What a refused submission says to the customer.
 *
 * The platform's own stated reason, verbatim, in every case it states one — it names the
 * file type it will take and the size it will not exceed, which is exactly what the customer
 * needs. The one exception is an expired session: `Unauthorized` is the platform talking to a
 * client, not to a person. Mapped here only, on this screen's failure path; a global 401
 * policy would touch every screen and belongs to its own change.
 */
function refusalWording(err: unknown): string {
  if (err instanceof ApiError && err.status === 401) {
    return 'Your session has expired. Please sign in again and retry.';
  }
  // R9: the impersonation guard replies with a code and no words at all, so there is no
  // stated reason to pass on — this is the one refusal the app has to phrase itself.
  if (err instanceof ApiError && err.code === 'IMPERSONATION_ACTION_BLOCKED') {
    return 'Documents cannot be submitted while an administrator is impersonating this account. The customer must submit them from their own session.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Upload failed';
}

/**
 * 059: identity documents in the customer app.
 *
 * The platform has accepted, stored, served and reviewed these documents since 017, and the
 * admin panel has both an upload and a review screen — but the app never grew a surface, so
 * every customer account was permanently unverified. This is that surface.
 */
/**
 * 116: the message shown only when Book Now sent the customer here (`?blocked=booking`) — the
 * page's own status badge/rejection-reason display already says WHAT the status is; this says
 * WHY they landed here unprompted.
 */
function blockedBookingMessage(status: IdentityStatus): string | null {
  switch (status) {
    case 'pending':
      return 'Upload your identity documents to book a car.';
    case 'submitted':
    // 199 (#288): a replacement on an approved record reads the same as any other
    // review-in-progress state to the customer (identity/status.ts's own choice, mirrored here).
    case 'resubmitted':
      return 'Your documents are under review. You can book once they are approved.';
    case 'rejected':
      return 'Your documents were rejected. Resubmit them to book a car.';
    case 'approved':
      return null;
  }
}

export function IdentityPage(_props: IdentityPageProps) {
  const goToSignIn = useSignInRedirect();
  const blockedByBooking = new URLSearchParams(window.location.search).get('blocked') === 'booking';
  const summary = useFetch<IdentitySummary>('/users/me/identity');
  const data = summary.data;
  const loading = summary.status === 'loading' || summary.status === 'idle';
  // The platform refuses this write while impersonating, and refuses it wordlessly. An
  // action that cannot succeed is not offered; viewing and status stay, which is 036's
  // existing line — everything not denylisted proceeds as the target.
  const { isImpersonating } = useAuth();
  // Written from each submission's own response (R5) — never from a second read, which
  // could only race the write that produced the state being shown.
  const [submitted, setSubmitted] = useState<Record<string, boolean>>({});
  const [busySide, setBusySide] = useState<IdentitySide | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  // Any accepted submission re-opens review and clears the previous decision server-side,
  // so a rejection's reason must stop being presented as current at that same moment.
  const [afterSubmit, setAfterSubmit] = useState<IdentityStatus | null>(null);
  const [viewing, setViewing] = useState<IdentitySide | null>(null);
  const [pendingReplacement, setPendingReplacement] = useState<{
    side: IdentitySide;
    file: File;
    input: HTMLInputElement;
  } | null>(null);

  const [submittingForReview, setSubmittingForReview] = useState(false);
  const [submitFailure, setSubmitFailure] = useState<string | null>(null);

  // 209 (#305): saved independently of any document upload and of the /submit gate — its own
  // action, mirroring submitForReview()'s shape (own busy/error state).
  const [studentId, setStudentId] = useState('');
  const [licenceExpiry, setLicenceExpiry] = useState('');
  const [detailsPrefilled, setDetailsPrefilled] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  useEffect(() => {
    if (data && !detailsPrefilled) {
      setStudentId(data.student_id ?? '');
      setLicenceExpiry(data.driving_licence_expires_at?.slice(0, 10) ?? '');
      setDetailsPrefilled(true);
    }
  }, [data, detailsPrefilled]);

  const sides: Record<string, boolean> = { ...(data?.sides ?? {}), ...submitted };
  const status: IdentityStatus = afterSubmit ?? data?.status ?? 'pending';
  const rejectionReason = afterSubmit ? undefined : data?.rejection_reason;
  const view = statusView(status);
  // After a successful submit the local status wins, and nothing is outstanding any more.
  const missing = afterSubmit ? [] : (data?.missing_for_submission ?? []);
  const blockedMessage = blockedByBooking ? blockedBookingMessage(status) : null;
  // 378 (#378): the customer typed something into a field but has not pressed Save yet — the
  // "Still needed" line alone reads identically whether the field was never touched or was typed
  // and left unsaved, and the second case is the one the issue reports as silent/unclear.
  const detailsDirty =
    detailsPrefilled &&
    (studentId !== (data?.student_id ?? '') ||
      licenceExpiry !== (data?.driving_licence_expires_at?.slice(0, 10) ?? ''));
  // 379 (#379): the record can be `approved` and still have a passed expiry date — computed
  // server-side (users/identity.ts), read here rather than re-derived from the raw date.
  const licenceExpired = Boolean(data?.licence_expired);

  /**
   * 181 US1: the customer says they are ready. Uploading is a draft; this is the deliberate act
   * that puts the documents in front of staff and notifies them.
   */
  async function submitForReview() {
    setSubmittingForReview(true);
    setSubmitFailure(null);
    try {
      const result = (await api.post('/users/me/identity/submit', {})) as {
        status: IdentityStatus;
      };
      setAfterSubmit(result.status);
    } catch (err) {
      setSubmitFailure(refusalWording(err));
    } finally {
      setSubmittingForReview(false);
    }
  }

  /**
   * 209 (#305): saves the licence expiry date and student ID, independent of any document.
   *
   * 🔴 378 (#378): this used to leave `data` (and its `missing_for_submission`) exactly as the
   * initial `GET /users/me/identity` returned it, so the Submit gate below stayed on stale
   * information until the whole page reloaded — a customer could type the expiry date, click
   * Save, and still see "Still needed: a driving licence expiry date". `summary.retry()` re-reads
   * the same endpoint this save just changed, so the gate reflects what was actually saved.
   */
  async function saveDetails() {
    setSavingDetails(true);
    setDetailsError(null);
    try {
      await api.patch('/users/me/identity/details', {
        student_id: studentId,
        driving_licence_expires_at: licenceExpiry,
      });
      // `retry()` only bumps a counter — it does not return a promise for the refetch itself.
      summary.retry();
    } catch (err) {
      setDetailsError(refusalWording(err));
    } finally {
      setSavingDetails(false);
    }
  }

  /** Asked only when approved: a confirmation on every replacement is one customers learn to
   *  dismiss without reading. 181 changed WHY it is asked — replacing no longer costs the
   *  approval, but it does leave a document nobody has reviewed under an approved record, which
   *  is worth a deliberate moment. */
  function onSelect(side: IdentitySide, file: File, input: HTMLInputElement) {
    if (status === 'approved') {
      setPendingReplacement({ side, file, input });
      return;
    }
    void submit(side, file, input);
  }

  async function submit(side: IdentitySide, file: File, input: HTMLInputElement) {
    setBusySide(side);
    setFailure(null);
    try {
      const prepared = await prepareIdentityFile(file);
      const form = new FormData();
      form.append('file', prepared, prepared.name);
      const result = (await api.upload(`/users/me/identity/${side}`, form)) as UploadResponse;
      setSubmitted((prev) => ({ ...prev, [side]: result.provided !== false }));
      setAfterSubmit(result.status);
    } catch (err) {
      setFailure(refusalWording(err));
    } finally {
      setBusySide(null);
      // A picker fires no change event for the same file twice, so without this a customer
      // who picked the wrong side could not simply pick the same photo again.
      input.value = '';
    }
  }

  return (
    <div className="bg-bg px-4 py-6">
      <PageHeader title="Identity Documents" fallback="/account" />

      {blockedMessage && (
        <p data-testid="identity-blocked-banner" className="mb-4 text-sm text-yellow-400">
          {blockedMessage}
        </p>
      )}

      {/* 379 (#379): an APPROVED record can still have a passed expiry date — the status badge
          alone would read as a plain green "Approved" with nothing telling the customer why
          booking is refused. Shown regardless of how they reached this page. */}
      {status === 'approved' && licenceExpired && (
        <p data-testid="identity-licence-expired-banner" className="mb-4 text-sm text-yellow-400">
          Your driving licence has expired. Update the expiry date below and save to book again.
        </p>
      )}

      <div className="mb-4 flex items-center gap-2">
        <span className="text-sm text-gray-400">Verification</span>
        <Badge variant={view.variant}>
          <span data-testid="identity-status">{view.label}</span>
        </Badge>
      </div>

      {rejectionReason && (
        <p data-testid="identity-rejection-reason" className="mb-4 text-sm text-red-400">
          {rejectionReason}
        </p>
      )}

      {isImpersonating && (
        <p className="mb-4 text-sm text-gray-400">
          You cannot submit documents while impersonating this customer. They must be submitted from
          the customer's own session.
        </p>
      )}

      {failure && (
        <p role="alert" className="mb-4 text-sm text-red-400">
          {failure}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : summary.status !== 'ok' ? (
        /* 064: the read's own outcome, in the shared wording (FR-011). A document list built
           from nothing would state that nothing has been submitted — a claim the platform
           never made. */
        <ResultState state={summary} onRetry={summary.retry} onSignIn={goToSignIn} />
      ) : (
        <div className="flex flex-col gap-3">
          {IDENTITY_GROUPS.map((group) => (
            <DocumentGroupCard
              key={group.key}
              group={group}
              provided={sides}
              requirementLabel={requirementLabel(group, data?.side_requirements)}
              canSubmit={!isImpersonating}
              busySide={busySide}
              onSelect={onSelect}
              onView={setViewing}
            />
          ))}
        </div>
      )}

      {/* 209 (#305): structured data alongside the images — saved independently, its own action. */}
      {summary.status === 'ok' && (
        <div className="mt-6 flex flex-col gap-2">
          <label className="text-sm text-gray-400" htmlFor="identity-student-id-input">
            Student ID (optional)
          </label>
          <input
            id="identity-student-id-input"
            data-testid="identity-student-id"
            type="text"
            className="rounded-lg bg-surface-card px-3 py-2 text-sm text-text"
            value={studentId}
            disabled={isImpersonating}
            onInput={(e) => setStudentId((e.target as HTMLInputElement).value)}
          />
          <label className="text-sm text-gray-400" htmlFor="identity-licence-expiry-input">
            Driving licence expiry date (required)
          </label>
          <input
            id="identity-licence-expiry-input"
            data-testid="identity-licence-expiry"
            type="date"
            className="rounded-lg bg-surface-card px-3 py-2 text-sm text-text"
            value={licenceExpiry}
            disabled={isImpersonating}
            onInput={(e) => setLicenceExpiry((e.target as HTMLInputElement).value)}
          />
          {detailsError && (
            <p role="alert" data-testid="identity-details-error" className="text-sm text-error">
              {detailsError}
            </p>
          )}
          <Button
            data-testid="identity-save-details"
            disabled={savingDetails || isImpersonating}
            onClick={() => void saveDetails()}
          >
            {savingDetails ? 'Saving…' : 'Save details'}
          </Button>
        </div>
      )}

      {/* 181 US1: the deliberate act. Uploading is a draft — before this feature the FIRST of nine
          documents flipped the record to "In review", so staff were shown unfinished submissions
          and the customer never chose a moment to be reviewed. */}
      {summary.status === 'ok' &&
        status !== 'submitted' &&
        status !== 'approved' &&
        // 199 (#288): replacing a document on an approved record already re-submits it at
        // upload time (server-side) — a "Submit for review" button here would be a stray,
        // redundant control for a submission that already happened automatically.
        status !== 'resubmitted' && (
          <div className="mt-6 flex flex-col gap-2">
            {/* 378 (#378): a customer who typed a value but never pressed Save saw the exact same
                "Still needed" text as one who never entered it at all — nothing told them the fix
                was to click Save first, which the field they just edited being the one still
                listed cannot make clear on its own. */}
            {detailsDirty && (
              <p data-testid="identity-details-unsaved-hint" className="text-sm text-yellow-400">
                You have unsaved changes — click Save details above before submitting for review.
              </p>
            )}
            {missing.length > 0 && (
              /* 🔴 The screen NAMES what is still needed. A disabled button with no explanation
               leaves the customer with nothing to act on (FR-005), and the list comes from the
               platform so it cannot disagree with the refusal the server would give. */
              <p data-testid="identity-missing" className="text-sm text-gray-400">
                Still needed: {missing.join(', ')}.
              </p>
            )}
            {submitFailure && (
              <p role="alert" data-testid="identity-submit-error" className="text-sm text-error">
                {submitFailure}
              </p>
            )}
            <Button
              data-testid="identity-submit-for-review"
              disabled={missing.length > 0 || submittingForReview || isImpersonating}
              onClick={() => void submitForReview()}
            >
              {submittingForReview ? 'Submitting…' : 'Submit for review'}
            </Button>
          </div>
        )}

      {viewing && (
        <DocumentPreviewSheet
          side={viewing}
          title={sideTitle(viewing)}
          onClose={() => setViewing(null)}
        />
      )}

      <ConfirmDialog
        open={pendingReplacement !== null}
        title="Replace an approved document?"
        // 🔴 181 CORRECTED this wording. It used to say "Replacing one sends them back for review,
        // and the current approval is lost" — true of the old backend, where any upload flipped the
        // record to `submitted`. It no longer happens: an upload changes no status at all, so an
        // approved customer keeps their approval and their ability to book (spec FR-001). Leaving
        // the old sentence would have been the screen telling the customer something false.
        body="Your documents are approved. Replacing one keeps your approval — send them for review again if you want the new document checked."
        confirmLabel="Replace"
        cancelLabel="Keep current"
        onCancel={() => {
          if (pendingReplacement) pendingReplacement.input.value = '';
          setPendingReplacement(null);
        }}
        onConfirm={() => {
          const replacement = pendingReplacement;
          setPendingReplacement(null);
          if (replacement) {
            void submit(replacement.side, replacement.file, replacement.input);
          }
        }}
      />
    </div>
  );
}
